import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  CreditCard,
  FileQuestion,
  LifeBuoy,
  Mail,
  ShieldCheck,
  Trophy,
} from "lucide-react";
import { Link } from "wouter";

const supportTopics = [
  {
    title: "Account access",
    description:
      "Get help with sign in, profile setup, email verification, and account recovery.",
    icon: LifeBuoy,
  },
  {
    title: "Payments and withdrawals",
    description:
      "Questions about wallet balances, Stripe verification, payout methods, or withdrawal status.",
    icon: CreditCard,
  },
  {
    title: "Tournament disputes",
    description:
      "Report prize concerns, judging issues, rule violations, or cancellation questions.",
    icon: Trophy,
  },
  {
    title: "Safety reports",
    description:
      "Flag cheating, fraud, inappropriate content, or suspicious multi-account activity.",
    icon: ShieldCheck,
  },
];

export default function Support() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <PublicNav />

      <main className="pt-28 pb-20 px-6">
        <section className="max-w-4xl mx-auto text-center mb-14">
          <div className="w-14 h-14 rounded-xl bg-neon/10 border border-neon/25 flex items-center justify-center mx-auto mb-5">
            <LifeBuoy className="w-7 h-7 text-neon" />
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold uppercase mb-4">
            GamePoint Support
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Need help with your account, a tournament, a payment, or a safety
            concern? Send us the details and the GamePoint team will route it to
            the right place.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <a href="mailto:support@gamepoint.com">
              <Button className="bg-neon hover:bg-neon-dim text-background font-bold gap-2">
                <Mail className="w-4 h-4" />
                Email Support
              </Button>
            </a>
            <Link href="/faq">
              <Button
                variant="outline"
                className="border-neon/30 text-neon hover:bg-neon/10 gap-2"
              >
                <FileQuestion className="w-4 h-4" />
                Read FAQ
              </Button>
            </Link>
          </div>
        </section>

        <section className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {supportTopics.map((topic) => (
            <div
              key={topic.title}
              className="rounded-lg border border-white/10 bg-[#111627] p-5"
            >
              <div className="w-10 h-10 rounded-lg bg-neon/10 flex items-center justify-center mb-4">
                <topic.icon className="w-5 h-5 text-neon" />
              </div>
              <h2 className="font-bold text-white mb-2">{topic.title}</h2>
              <p className="text-sm text-slate-400 leading-relaxed">
                {topic.description}
              </p>
            </div>
          ))}
        </section>

        <section className="max-w-4xl mx-auto rounded-lg border border-amber-400/20 bg-amber-400/5 p-5 flex gap-4">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <h2 className="font-bold text-white mb-1">Include useful details</h2>
            <p className="text-sm text-slate-400 leading-relaxed">
              For the fastest response, include your account email, tournament
              name or link, payment amount if relevant, screenshots when useful,
              and a short description of what happened.
            </p>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
